import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import API_BASE_URL from '../config';
import './ChatPage.css';

export default function ChatPage() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [messages, setMessages] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [draft, setDraft] = useState('');
  const [status, setStatus] = useState('');

  const isAdmin = String(user?.userType || '').toLowerCase() === 'admin';

  const token = useMemo(() => localStorage.getItem('token'), []);

  useEffect(() => {
    const rawUser = localStorage.getItem('user');
    if (!token || !rawUser) {
      navigate('/login', { state: { from: '/chat' } });
      return;
    }
    try {
      const parsed = JSON.parse(rawUser);
      setUser(parsed);
      if (String(parsed?.userType || '').toLowerCase() !== 'admin') {
        setSelectedUserId(parsed?.id || parsed?.userId || null);
      }
    } catch (_err) {
      navigate('/login', { state: { from: '/chat' } });
    }
  }, [navigate, token]);

  useEffect(() => {
    const checkChatHealth = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/chat/health`);
        if (!res.ok) {
          setStatus('Chat backend is not ready. Please deploy backend updates.');
        }
      } catch (err) {
        setStatus(`Cannot reach chat backend: ${err?.message || 'network error'}`);
      }
    };
    checkChatHealth();
  }, []);

  const authHeaders = useMemo(
    () => ({
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    }),
    [token]
  );

  const fetchConversations = async () => {
    if (!isAdmin) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/chat/conversations`, {
        headers: authHeaders,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) return;
      const list = data.conversations || [];
      setConversations(list);
      if (!selectedUserId && list.length > 0) {
        setSelectedUserId(list[0].UserId);
      }
    } catch (_err) {
      // ignore poll failures
    }
  };

  const fetchMessages = async () => {
    try {
      const query = isAdmin && selectedUserId ? `?userId=${selectedUserId}` : '';
      const res = await fetch(`${API_BASE_URL}/api/chat/messages${query}`, { headers: authHeaders });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) return;
      setMessages(data.messages || []);
    } catch (_err) {
      // ignore poll failures
    }
  };

  useEffect(() => {
    if (!user) return;
    fetchConversations();
    fetchMessages();
    const id = setInterval(() => {
      fetchConversations();
      fetchMessages();
    }, 5000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, selectedUserId, isAdmin]);

  const handleSend = async (e) => {
    e.preventDefault();
    const message = draft.trim();
    if (!message) return;
    if (isAdmin && !selectedUserId) {
      setStatus('Select a user conversation first.');
      return;
    }

    try {
      const payload = isAdmin ? { userId: selectedUserId, message } : { message };
      const res = await fetch(`${API_BASE_URL}/api/chat/messages`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) {
        if (res.status === 401 || res.status === 403) {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          setStatus('Session expired. Please login again.');
          navigate('/login', { state: { from: '/chat' } });
          return;
        }
        const serverMessage = data && (data.error || data.message || data.details);
        if (serverMessage) {
          setStatus(serverMessage);
        } else {
          setStatus(`Failed to send message (HTTP ${res.status}). Backend may not be updated.`);
        }
        return;
      }
      setDraft('');
      setStatus('');
      await fetchMessages();
      if (isAdmin) await fetchConversations();
    } catch (err) {
      setStatus(`Failed to send message: ${err?.message || 'network error'}`);
    }
  };

  return (
    <div className="chat-page">
      <div className="chat-shell">
        <div className="chat-top">
          <button type="button" onClick={() => navigate('/')} className="chat-back-btn">
            ← Home
          </button>
          <h1>{isAdmin ? 'Admin Chat Inbox' : 'Chat with Admin'}</h1>
        </div>

        <div className="chat-layout">
          {isAdmin && (
            <aside className="chat-conversations">
              <h3>Conversations</h3>
              {conversations.length === 0 ? (
                <p className="chat-empty-small">No messages yet.</p>
              ) : (
                conversations.map((c) => (
                  <button
                    key={c.UserId}
                    type="button"
                    className={`chat-convo-item ${selectedUserId === c.UserId ? 'active' : ''}`}
                    onClick={() => setSelectedUserId(c.UserId)}
                  >
                    <strong>{c.Name || c.Email}</strong>
                    <span>{c.LastMessage || 'No message'}</span>
                    {!!c.UnreadCount && <em>{c.UnreadCount} unread</em>}
                  </button>
                ))
              )}
            </aside>
          )}

          <section className="chat-main">
            <div className="chat-messages">
              {messages.length === 0 ? (
                <p className="chat-empty">No messages yet. Start the chat.</p>
              ) : (
                messages.map((m) => (
                  <div
                    key={m.Id}
                    className={`chat-bubble ${
                      (isAdmin && m.SenderRole === 'admin') || (!isAdmin && m.SenderRole === 'user')
                        ? 'mine'
                        : 'theirs'
                    }`}
                  >
                    <p>{m.Message}</p>
                    <small>{new Date(m.CreatedAt).toLocaleString()}</small>
                  </div>
                ))
              )}
            </div>

            <form className="chat-input-row" onSubmit={handleSend}>
              <input
                type="text"
                placeholder="Type your message..."
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
              />
              <button type="submit">Send</button>
            </form>
            {status && <p className="chat-status">{status}</p>}
          </section>
        </div>
      </div>
    </div>
  );
}
